import React from "react";
import PropTypes from "prop-types";
import ReactGA from "react-ga";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import CircularProgress from "@material-ui/core/CircularProgress";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Divider from "@material-ui/core/Divider";
import { TextField, Grid, Button, DialogActions } from "@material-ui/core";
import MediaQuery from "react-responsive";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import { useHistory } from "react-router-dom";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import { Checkbox, FormGroup, FormControlLabel } from "@material-ui/core";
import FilterListIcon from "@material-ui/icons/FilterList";
import { useMediaQuery } from "react-responsive";
import { Link } from "react-router-dom";

import PolicyDialog from '../Pages/Home/HomeComponents/PolicyDialog';
import StatusTableRow from './StatusTableRow';

import { NotMobile } from '../utils';

const ROWS_PER_PAGE = 10;

function desc(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function stableSort(array, cmp, searchTerm, filters) {
  // If there are no filters then just do regular sorting
  if (filters.length === 0 && searchTerm === "") {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = cmp(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return { newTableRows: stabilizedThis.map((el) => el[0]) };
  }
  // Handle remote work policy
  let rows = array
    .filter((item) => {
      // No policy filters, so don't filter
      if (
        !filters.includes("fully-distributed") &&
        !filters.includes("remote-first") &&
        !filters.includes("remote-friendly") &&
        !filters.includes("temp-remote")
      )
        return item;
      // First filter by remote policy
      if (
        filters.includes("fully-distributed") &&
        item.status.includes("Fully Distributed")
      )
        return item;
      if (
        filters.includes("remote-first") &&
        item.status.includes("Remote-First")
      )
        return item;
      if (
        filters.includes("remote-friendly") &&
        item.status.includes("Remote-Friendly")
      )
        return item;
      if (
        filters.includes("temp-remote") &&
        item.status.includes("Temporarily Remote")
      )
        return item;
    })
    .filter((item) => {
      // No company size filters, so don't filter
      if (
        !filters.includes("1-100 Employees") &&
        !filters.includes("101-1k Employees") &&
        !filters.includes("1k-10k Employees") &&
        !filters.includes("10k+ Employees")
      )
        return item;
      // Company size filter, so filter
      if (item.numEmployees) {
        let number = Number(parseFloat(item.numEmployees.replace(/,/g, "")));
        if (filters.includes("1-100 Employees") && number > 0 && number <= 100)
          return item;
        if (
          filters.includes("101-1k Employees") &&
          number > 100 &&
          number <= 1000
        )
          return item;
        if (
          filters.includes("1k-10k Employees") &&
          number > 1000 &&
          number <= 10000
        )
          return item;
        if (filters.includes("10k+ Employees") && number > 10000) return item;
      }
    })
    .filter((item) => {
      // No search term filter
      if (searchTerm === "") return item;
      let lengthOfSearchTerm = searchTerm.length;
      return item.name
        .substring(0, lengthOfSearchTerm)
        .toUpperCase()
        .includes(searchTerm.toUpperCase());
    });
  return { newTableRows: rows };
}

function getSorting(order, orderBy) {
  return order === "desc"
    ? (a, b) => desc(a, b, orderBy)
    : (a, b) => -desc(a, b, orderBy);
}

function EnhancedTableHead(props) {
  const { classes, order, orderBy, rowCount, onRequestSort, headCells } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell, idx) => {
          const align = idx === headCells.length - 1 ? "right" : "left";
          return (
            <TableCell
              key={headCell.id}
              align={align}
              padding={headCell.disablePadding ? "none" : "default"}
              sortDirection={orderBy === headCell.id ? order : false}
              className={classes.companyName}
            >
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={order}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
              </TableSortLabel>
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
  headCells: PropTypes.array.isRequired,
};

const useStyles = makeStyles((theme) => ({
  root: {
    // border: "1px solid rgb(65, 114, 227)"
  },
  toolbarGrid: {
    padding: 16,
  },
  tableWrapper: {
    overflowX: "scroll",
  },
  searchBarContainer: {
    textAlign: "left",
    [theme.breakpoints.down("sm")]: {
      order: 2,
    },
  },
  filtersContainer: {
    [theme.breakpoints.down("sm")]: {
      order: 1,
    },
  },
  AddCompanyButton: {
    textAlign: "right",
  },
  tableTitle: {
    [theme.breakpoints.down("sm")]: {
      fontSize: 18,
    },
  },
  companyName: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    paddingLeft: 12,
    paddingRight: 12,
  },
  filterBtn: {
    borderRadius: 25,
    marginRight: "8px",
    textTransform: "none",
  },
  tableRow: {
    '&:hover': {
      backgroundColor: "#1a73e842 !important",
    }
  },
  "@media (max-width: 767px)": {
    AddCompanyButton: {
      display: "flex",
      justifyContent: "space-between",
      padding: "10px 0 0 10px"
    }
  }
}));

export default function StatusTable(props) {
  const classes = useStyles();
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("cost");
  let [page, setPage] = React.useState(0);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [filters, setFilters] = React.useState([]);
  const [rowsPerPage, setRowsPerPage] = React.useState(ROWS_PER_PAGE);
  const [searched, setSearched] = React.useState(false);
  const { headCells, rows, searchBarEnabled, orderTable, dense, fullWidth, loading } = props;
  const [moreFilters, setMoreFilters] = React.useState(false);
  const [policyOpen, setPolicyOpen] = React.useState(false);
  const [companySizeFilter, setCompanySizeFilter] = React.useState({
    "1-100 Employees": false,
    "101-1k Employees": false,
    "1k-10k Employees": false,
    "10k+ Employees": false,
  });

  const isMobileLayout = useMediaQuery({
    query: "(max-width: 700px)",
  });

  let history = useHistory();

  const fullscreen = props.fullscreen;

  const closePolicyDialog = () => setPolicyOpen(false);
  const openPolicyDialog = () => setPolicyOpen(true);

  function moreFilterBtn() {
    // Analytics
    logAnalyticsEvent({
      category: "Filter Clicks",
      action: "More Filters Button Clicked",
      label: !moreFilters === true ? "Open Filter" : "Close Filter",
    });
    setMoreFilters(!moreFilters);
  }

  function filterBtn(e) {
    // Set filters to current filters
    let newFilters = filters.length === 0 ? [] : filters;
    if (e.currentTarget.id === "dialogFilter") {
      Object.keys(companySizeFilter).forEach((key) => {
        // If the company size filter is selected, add it
        if (companySizeFilter[key] && !newFilters.includes(key)) {
          newFilters.push(key);
          // Analytics
          logAnalyticsEvent({
            category: "Filter Clicks",
            action: "Filter Button Clicked",
            label: key,
          });
        } else if (!companySizeFilter[key] && newFilters.includes(key)) {
          // This filter shouldn't be in the array
          const index = newFilters.indexOf(key);
          newFilters.splice(index, 1);
        }
      });
      handleClose();
      setFilters([...newFilters]);
    } else {
      // Handle toggle by removing filter
      if (filters.includes(e.currentTarget.id)) {
        newFilters = filters.filter((item) => item !== e.currentTarget.id);
      } else {
        // Doesn't include current filter so add it
        newFilters.push(e.currentTarget.id);
        // Analytics
        logAnalyticsEvent({
          category: "Filter Clicks",
          action: "Filter Button Clicked",
          label: e.currentTarget.id,
        });
      }
      setFilters([...newFilters]);
    }
    setPage(0);
  }

  function logAnalyticsEvent({ category, action, label }) {
    if (
      window.location.hostname !== "localhost" &&
      window.location.hostname !== "127.0.0.1"
    ) {
      ReactGA.event({
        category,
        action,
        label,
      });
    }
  }


  function goToJobsPage(companyName) {
    logAnalyticsEvent({
      category: "Jobs Link",
      action: "Jobs Link Clicked",
      label: `Main Table for ${companyName}`,
    });
  }

  function goToSourcePage(companyName) {
    logAnalyticsEvent({
      category: "Source Link",
      action: "Source Link Clicked",
      label: `Main Table for ${companyName}`,
    });
  }

  const handleRequestSort = (event, property) => {
    logAnalyticsEvent({
      category: "Table Sort",
      action: "Sort Button Clicked",
      label: property === "status" ? "remotePolicy" : property,
    });
    const isDesc = orderBy === property && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchQuery = (event) => {
    setSearchTerm(event.target.value);
  };

  const renderTableBody = (order, orderBy, searchTerm, filters) => {
    let { newTableRows } = stableSort(
      rows,
      getSorting(order, orderBy),
      searchTerm,
      filters
    );
    const tableRows = newTableRows
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      .map((row, index) => (
        <StatusTableRow row={row} />
      ));
    // Analytics
    if (searchTerm !== "") {
      // Log when they start searching
      if (!searched) {
        // logAnalyticsEvent({
        //   category: "Company Search",
        //   action: "Started Searching",
        //   label: "Undefined",
        // });
        setSearched(true);
      }
      // Log all search terms
      // logAnalyticsEvent({
      //   category: "Company Search",
      //   action: "Partial Search",
      //   label: searchTerm.toUpperCase(),
      // });
      if (
        tableRows.length === 1 &&
        tableRows[0].props.row.name.toUpperCase() === searchTerm.toUpperCase()
      ) {
        logAnalyticsEvent({
          category: "Company Search",
          action: "Exact Company Found",
          label: searchTerm.toUpperCase(),
        });
      }
    }
    return [tableRows, newTableRows];
  };

  const getFormElements = () => {
    let companySizeArr = [
      "Company Size",
      "1-100 Employees",
      "101-1k Employees",
      "1k-10k Employees",
      "10k+ Employees",
    ];
    return companySizeArr;
  };

  const handleClose = () => {
    setMoreFilters(false);
  };

  const handleFilterCheck = (e) => {
    setCompanySizeFilter({
      ...companySizeFilter,
      [e.currentTarget.id]: !companySizeFilter[e.currentTarget.id],
    });
  };

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

  const isFullyDistributed = filters.includes("fully-distributed");
  const isRemoteFirst = filters.includes("remote-first");
  const isRemoteFriendly = filters.includes("remote-friendly");
  const isTempRemote = filters.includes("temp-remote");
  const isMoreFilters =
    filters.includes("1-100 Employees") ||
    filters.includes("101-1k Employees") ||
    filters.includes("1k-10k Employees") ||
    filters.includes("10k+ Employees")
      ? true
      : false;

  let [tableRows, newTableRows] = renderTableBody(
    order,
    orderBy,
    searchTerm,
    filters
  );
  let companyArr = getFormElements();

  if (moreFilters) {
    return (
      <Dialog open={true} onClose={handleClose} fullWidth>
        <DialogTitle style={{ alignSelf: "center" }}>More Filters</DialogTitle>
        <Divider />
        <Grid
          container
          style={{
            marginLeft: "50px",
            marginRight: "50px",
            marginBottom: "25px",
          }}
        >
          {companyArr.map((item) =>
            item === "Company Size" ? (
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  style={{ textAlign: "left", marginTop: "25px" }}
                >
                  {item}
                </Typography>
              </Grid>
            ) : (
              <Grid item xs={12} align="left">
                <FormGroup component="fieldset">
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        id={item}
                        checked={companySizeFilter[item]}
                        onChange={handleFilterCheck}
                        size="small"
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body1" align="left">
                        {item}
                      </Typography>
                    }
                    labelPlacement="end"
                  />
                </FormGroup>
              </Grid>
            )
          )}
          <Divider light />
        </Grid>
        <DialogActions style={{ justifyContent: "space-between", margin: 16 }}>
          <Button variant="contained" onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button
            variant="contained"
            id="dialogFilter"
            onClick={filterBtn}
            color="primary"
          >
            Add Filters
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  const TableCenter = () => {
    if (loading) {

      const loadingStyle = { 
        height: (dense ? 33 : 53) * 10,
      }
      return (
        <TableRow style={loadingStyle}>
          <TableCell colSpan={12}>
            <div style={{ position: "absolute", left: 0, right: 0, display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </div>
          </TableCell>
        </TableRow>
      )
    }

    return (
      <TableBody>
        {tableRows}
        {emptyRows > 0 && (
          <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}>
            <TableCell colSpan={6} />
          </TableRow>
        )}
      </TableBody>
    )
  }

  const fullscreenStyle = props.fullscreen ? { justifyContent: "flexEnd" } : null;

  return (
    <div className={classes.root} style={{ width: "100%", boxShadow: "0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)" }}>
      <PolicyDialog open={policyOpen} onClose={closePolicyDialog} />
      <Paper className={classes.paper}>
        <Toolbar
          disableGutters
          variant={dense ? "dense" : "regular"}
          className={classes.tableToolbar}
        >
          <Grid
            container
            alignItems="flex-end"
            justify="left"
            className={classes.toolbarGrid}
            spacing={3}
          >
            <NotMobile>
              <Grid item xs={6}>
                <Typography variant="h6" className={classes.tableTitle}>
                  {/* <b>Remote Status</b> */}
                </Typography>
              </Grid>
            </NotMobile>
            <Grid item xs={12} sm={6} className={classes.AddCompanyButton} style={fullscreenStyle}>
              <Button
                href="#inputForm"
                variant="contained"
                color="primary"
                className={classes.filterBtn}
                onClick={openPolicyDialog}
              >
                <AddIcon style={{ marginRight: 8 }} />
                Contribute
                {/* Changed to contribute because looks better on mobile, yes this is the best solution i came up with */}
              </Button>
              { 
                fullscreen && (
                  <Link to="/table" style={{ textDecoration: "none" }}>
                    <Button
                      href="#inputForm"
                      variant="contained"
                      color="primary"
                      className={classes.filterBtn}
                    >
                      Full Screen
                    </Button>              
                  </Link> 
                )
              }
            </Grid>            
            <Grid
              item
              xs={12}
              sm={12}
              md={4}
              className={classes.searchBarContainer}
            >
              <TextField
                InputProps={{
                  startAdornment: <SearchIcon style={{ marginRight: 8 }} />,
                }}
                size="small"
                fullWidth
                variant="standard"
                placeholder="Search"
                onChange={handleSearchQuery}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={12}
              md={8}
              clasName={classes.filtersContainer}
            >
              <MediaQuery minWidth={750}>
                <Grid container justify="flex-end">
                  <Button
                    variant="contained"
                    id="fully-distributed"
                    variant={isFullyDistributed ? "contained" : "outlined"}
                    size="small"
                    color="primary"
                    onClick={filterBtn}
                    className={classes.filterBtn}
                  >
                    Fully Distributed
                  </Button>
                  <Button
                    variant="contained"
                    id="remote-first"
                    variant={isRemoteFirst ? "contained" : "outlined"}
                    size="small"
                    color="primary"
                    onClick={filterBtn}
                    className={classes.filterBtn}
                  >
                    Remote-First
                  </Button>
                  <Button
                    variant="contained"
                    id="remote-friendly"
                    variant={isRemoteFriendly ? "contained" : "outlined"}
                    size="small"
                    color="primary"
                    onClick={filterBtn}
                    className={classes.filterBtn}
                  >
                    Remote-Friendly
                  </Button>
                  <Button
                    variant="contained"
                    id="temp-remote"
                    variant={isTempRemote ? "contained" : "outlined"}
                    size="small"
                    color="primary"
                    onClick={filterBtn}
                    className={classes.filterBtn}
                  >
                    Temp Remote
                  </Button>
                  <Button
                    variant="contained"
                    id="more-filters"
                    variant={isMoreFilters ? "contained" : "outlined"}
                    size="small"
                    color={"primary"}
                    onClick={moreFilterBtn}
                    className={classes.filterBtn}
                  >
                    More Filters{" "}
                    <FilterListIcon
                      style={{ marginLeft: "4px" }}
                      fontSize="small"
                    />{" "}
                  </Button>
                </Grid>
              </MediaQuery>
            </Grid>
          </Grid>
        </Toolbar>
        <div className={classes.tableWrapper}>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size={dense ? "small" : "medium"}
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
              headCells={headCells}
            />
            <TableCenter />
          </Table>
        </div>
        {isMobileLayout && (
          <TablePagination
            rowsPerPageOptions={[10]}
            component="div"
            count={newTableRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            backIconButtonProps={{
              "aria-label": "previous page",
            }}
            nextIconButtonProps={{
              "aria-label": "next page",
            }}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        )}
        {!isMobileLayout && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={newTableRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            backIconButtonProps={{
              "aria-label": "previous page",
            }}
            nextIconButtonProps={{
              "aria-label": "next page",
            }}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        )}
      </Paper>
    </div>
  );
}
